/**
 * ConnectionStatusIndicator Component
 *
 * A small, unobtrusive indicator showing WebSocket connection status.
 * Only visible when there's a connection issue (disconnected, reconnecting, error).
 */
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import SyncIcon from "@mui/icons-material/Sync";
import { Box, Chip, Fade, Tooltip, useTheme } from "@mui/material";
import React from "react";
import { WSConnectionState } from "../../types/websocket.types";

export interface ConnectionStatusIndicatorProps {
  /** Current connection state */
  state: WSConnectionState;
  /** Number of reconnection attempts (when reconnecting) */
  reconnectAttempts?: number;
  /** Maximum reconnection attempts */
  maxReconnectAttempts?: number;
  /** Always show indicator (even when connected) */
  alwaysShow?: boolean;
  /** Size variant */
  size?: "small" | "medium";
  /** Whether to show as chip or just icon */
  variant?: "chip" | "icon";
}

const ConnectionStatusIndicator: React.FC<ConnectionStatusIndicatorProps> = ({
  state,
  reconnectAttempts = 0,
  maxReconnectAttempts = 10,
  alwaysShow = false,
  size = "small",
  variant = "chip",
}) => {
  const theme = useTheme();

  const getStatusConfig = () => {
    switch (state) {
      case WSConnectionState.CONNECTED:
        return {
          icon: <CloudDoneIcon fontSize={size} />,
          label: "Conectado",
          tooltip: "Conexão em tempo real ativa",
          color: theme.palette.success.main,
          bgColor: theme.palette.success.light,
          show: alwaysShow,
        };
      case WSConnectionState.CONNECTING:
        return {
          icon: (
            <SyncIcon
              fontSize={size}
              sx={{
                animation: "spin 1s linear infinite",
                "@keyframes spin": {
                  from: { transform: "rotate(0deg)" },
                  to: { transform: "rotate(360deg)" },
                },
              }}
            />
          ),
          label: "Conectando...",
          tooltip: "Estabelecendo conexão...",
          color: theme.palette.info.main,
          bgColor: theme.palette.info.light,
          show: true,
        };
      case WSConnectionState.RECONNECTING:
        return {
          icon: (
            <SyncIcon
              fontSize={size}
              sx={{
                animation: "spin 1s linear infinite",
                "@keyframes spin": {
                  from: { transform: "rotate(0deg)" },
                  to: { transform: "rotate(360deg)" },
                },
              }}
            />
          ),
          label: `Reconectando (${reconnectAttempts}/${maxReconnectAttempts})`,
          tooltip: `Tentando reconectar... Tentativa ${reconnectAttempts} de ${maxReconnectAttempts}`,
          color: theme.palette.warning.main,
          bgColor: theme.palette.warning.light,
          show: true,
        };
      case WSConnectionState.DISCONNECTED:
        return {
          icon: <CloudOffIcon fontSize={size} />,
          label: "Desconectado",
          tooltip:
            "Sem conexão em tempo real. As atualizações podem estar atrasadas.",
          color: theme.palette.text.disabled,
          bgColor: theme.palette.action.disabledBackground,
          show: true,
        };
      case WSConnectionState.ERROR:
        return {
          icon: <CloudOffIcon fontSize={size} />,
          label: "Erro de conexão",
          tooltip: "Não foi possível conectar. Tente recarregar a página.",
          color: theme.palette.error.main,
          bgColor: theme.palette.error.light,
          show: true,
        };
      default:
        return {
          icon: <CloudOffIcon fontSize={size} />,
          label: "Desconhecido",
          tooltip: "Estado de conexão desconhecido",
          color: theme.palette.text.disabled,
          bgColor: theme.palette.action.disabledBackground,
          show: false,
        };
    }
  };

  const config = getStatusConfig();

  if (!config.show) {
    return null;
  }

  if (variant === "icon") {
    return (
      <Fade in={config.show}>
        <Tooltip title={config.tooltip} arrow>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              color: config.color,
              cursor: "help",
            }}
          >
            {config.icon}
          </Box>
        </Tooltip>
      </Fade>
    );
  }

  return (
    <Fade in={config.show}>
      <Tooltip title={config.tooltip} arrow>
        <Chip
          icon={config.icon}
          label={config.label}
          size={size}
          sx={{
            backgroundColor: config.bgColor,
            color: config.color,
            fontWeight: 500,
            fontSize: size === "small" ? "0.75rem" : "0.875rem",
            "& .MuiChip-icon": {
              color: config.color,
            },
          }}
        />
      </Tooltip>
    </Fade>
  );
};

export default ConnectionStatusIndicator;
